import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Tabs,
  Tab,
  Row,
  Col,
  Alert,
  ProgressBar,
  Dropdown,
} from "react-bootstrap";
import Loader from "../../components/common/Loader";
import api from "../../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  GeoAlt,
  Person,
  Pencil,
  Eye,
  Telephone,
  FileText,
  Plus,
  Filter,
  Download,
  Search,
  CheckCircle,
  XCircle,
  ClockHistory,
  CalendarCheck,
  CalendarX,
  SortDown,
  ThreeDotsVertical,
  Whatsapp,
  Envelope,
  ChevronDown,
  X,
} from "react-bootstrap-icons";

const AppointmentManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // New filter states
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCounselor, setSelectedCounselor] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [minDuration, setMinDuration] = useState("");
  const [maxDuration, setMaxDuration] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [availableCities, setAvailableCities] = useState([]);
  const [availableCounselors, setAvailableCounselors] = useState([]);

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);

  // Selected appointment states
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [status, setStatus] = useState("");
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState("zoom");

  // Edit form states
  const [editForm, setEditForm] = useState({
    scheduledDate: "",
    scheduledTime: "",
    counselor: "",
    location: "",
    notes: "",
    duration: 60,
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Pre-fill search from query params (e.g. ?appointmentId=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromSearch =
      params.get("appointmentId") || params.get("q") || params.get("search");
    if (fromSearch) {
      setSearchTerm(fromSearch);
    }
  }, [location.search]);

  useEffect(() => {
    if (appointments.length > 0) {
      extractFilterOptions();
    }
  }, [appointments]);

  const fetchAppointments = async () => {
    try {
      const response = await api.get("/appointments/admin");
      if (response.data.success) {
        setAppointments(response.data.appointments);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = () => {
    // Extract unique cities
    const cities = [...new Set(appointments.map(a => a.studentId?.city).filter(Boolean))];
    setAvailableCities(cities.sort());

    // Extract unique counselors
    const counselors = [...new Set(appointments.map(a => a.counselor).filter(Boolean))];
    setAvailableCounselors(counselors.sort());
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDateRange({ start: "", end: "" });
    setSelectedCity("");
    setSelectedCounselor("");
    setSelectedType("");
    setMinDuration("");
    setMaxDuration("");
    setSortBy("newest");
  };

  const fetchStudentDetails = async (studentId) => {
    // This function remains unchanged
  };

  const handleStatusUpdate = async () => {
    try {
      await api.put(`/appointments/admin/${selectedAppointment._id}`, {
        status: status,
      });
      fetchAppointments();
      setShowStatusModal(false);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleViewClick = (appointment) => {
    // Navigate to appointment detail page
    navigate(`/admin/appointments/${appointment._id}`, {
      state: { appointment },
    });
  };

  const handleEditClick = (appointment) => {
    setSelectedAppointment(appointment);
    setEditForm({
      scheduledDate: appointment.scheduledDate
        ? new Date(appointment.scheduledDate).toISOString().split("T")[0]
        : "",
      scheduledTime: appointment.scheduledTime || "",
      counselor: appointment.counselor || "",
      location: appointment.location || "",
      notes: appointment.notes || "",
      duration: appointment.duration || 60,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      await api.put(`/appointments/admin/${selectedAppointment._id}`, editForm);
      fetchAppointments();
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating appointment:", error);
    }
  };

  const handleStatusClick = (appointment) => {
    setSelectedAppointment(appointment);
    setStatus(appointment.status);
    setShowStatusModal(true);
  };

  const fetchDocuments = async (appointmentId) => {
    try {
      const response = await api.get(`/documents/${appointmentId}`);
      if (response.data.success) {
        setDocuments(response.data.data?.documents || []);
        setSelectedAppointment(
          appointments.find((a) => a.appointmentId === appointmentId),
        );
        setShowDocsModal(true);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge bg="success" className="px-3 py-2 fs-6 rounded-pill">
            <CheckCircle className="me-1" /> Confirmed
          </Badge>
        );
      case "pending":
        return (
          <Badge bg="warning" className="px-3 py-2 fs-6 rounded-pill">
            <ClockHistory className="me-1" /> Pending
          </Badge>
        );
      case "completed":
        return (
          <Badge bg="info" className="px-3 py-2 fs-6 rounded-pill">
            <CalendarCheck className="me-1" /> Completed
          </Badge>
        );
      case "cancelled":
        return (
          <Badge bg="danger" className="px-3 py-2 fs-6 rounded-pill">
            <CalendarX className="me-1" /> Cancelled
          </Badge>
        );
      case "rescheduled":
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            <Calendar className="me-1" /> Rescheduled
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            {status}
          </Badge>
        );
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "zoom":
        return (
          <Badge bg="info" className="px-3 py-2 fs-6 rounded-pill">
            <Telephone className="me-1" /> Phone Call
          </Badge>
        );
      case "office":
        return (
          <Badge bg="primary" className="px-3 py-2 fs-6 rounded-pill">
            <GeoAlt className="me-1" /> Office
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="px-3 py-2 fs-6 rounded-pill">
            {type}
          </Badge>
        );
    }
  };

  const formatDateTime = (date, time) => {
    if (!date) return "Not scheduled";
    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return time ? `${formattedDate} at ${time}` : formattedDate;
  };

  const formatPhoneForWhatsapp = (phone) => {
    if (!phone) return "";
    // Remove non-digit characters and leading plus
    const digits = phone.replace(/\D/g, "");
    return digits;
  };

  const getAppointmentDetails = (apt) => {
    const details = [];
    if (apt.scheduledDate)
      details.push({
        icon: <Calendar className="me-2" />,
        text: `Date: ${new Date(apt.scheduledDate).toLocaleDateString()}`,
      });
    if (apt.scheduledTime)
      details.push({
        icon: <Clock className="me-2" />,
        text: `Time: ${apt.scheduledTime}`,
      });
    if (apt.duration)
      details.push({
        icon: <Clock className="me-2" />,
        text: `Duration: ${apt.duration} minutes`,
      });
    if (apt.location)
      details.push({
        icon: <GeoAlt className="me-2" />,
        text: `Location: ${apt.location}`,
      });
    if (apt.counselor)
      details.push({
        icon: <Person className="me-2" />,
        text: `Counselor: ${apt.counselor}`,
      });
    return details;
  };

  const handleExportCSV = () => {
    // Create CSV headers
    const headers = [
      'Appointment ID',
      'Student Name',
      'Student Email',
      'Student Phone',
      'Student City',
      'Appointment Type',
      'Status',
      'Scheduled Date',
      'Scheduled Time',
      'Duration (mins)',
      'Counselor',
      'Location',
      'Notes',
      'Created At'
    ];

    // Prepare data
    const csvData = filteredAppointments.map(apt => [
      apt.appointmentId || 'N/A',
      apt.studentId?.name || 'N/A',
      apt.studentId?.email || 'N/A',
      apt.studentId?.phone || apt.studentId?.contact || apt.studentId?.phoneNumber || apt.phoneNumber || 'N/A',
      apt.studentId?.city || 'N/A',
      apt.appointmentType || 'N/A',
      apt.status || 'N/A',
      apt.scheduledDate ? new Date(apt.scheduledDate).toLocaleDateString() : 'N/A',
      apt.scheduledTime || 'N/A',
      apt.duration || 'N/A',
      apt.counselor || 'N/A',
      apt.location || 'N/A',
      apt.notes ? apt.notes.replace(/,/g, ';') : 'N/A', // Replace commas to avoid CSV issues
      apt.createdAt ? new Date(apt.createdAt).toLocaleString() : 'N/A'
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `appointments_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter appointments based on active tab and all filters
  const filteredAppointments = appointments.filter((apt) => {
    // Filter by tab
    if (activeTab === "zoom" && apt.appointmentType !== "zoom") return false;
    if (activeTab === "office" && apt.appointmentType !== "office") return false;

    // Filter by status
    if (statusFilter !== "all" && apt.status !== statusFilter) return false;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        apt.appointmentId?.toLowerCase().includes(searchLower) ||
        apt.studentId?.name?.toLowerCase().includes(searchLower) ||
        apt.studentId?.email?.toLowerCase().includes(searchLower) ||
        apt.studentId?.phone?.toLowerCase().includes(searchLower) ||
        apt.studentId?.contact?.toLowerCase().includes(searchLower) ||
        apt.studentId?.phoneNumber?.toLowerCase().includes(searchLower) ||
        apt.counselor?.toLowerCase().includes(searchLower) ||
        apt.location?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Filter by city
    if (selectedCity && apt.studentId?.city !== selectedCity) return false;

    // Filter by counselor
    if (selectedCounselor && apt.counselor !== selectedCounselor) return false;

    // Filter by appointment type (if not using tabs)
    if (selectedType && apt.appointmentType !== selectedType) return false;

    // Filter by duration range
    if (minDuration && apt.duration && apt.duration < parseInt(minDuration)) return false;
    if (maxDuration && apt.duration && apt.duration > parseInt(maxDuration)) return false;

    // Filter by date range
    if (dateRange.start && dateRange.end && apt.scheduledDate) {
      const aptDate = new Date(apt.scheduledDate);
      const startDate = new Date(dateRange.start);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      if (aptDate < startDate || aptDate > endDate) return false;
    } else if (dateRange.start && apt.scheduledDate) {
      const aptDate = new Date(apt.scheduledDate);
      const startDate = new Date(dateRange.start);
      if (aptDate < startDate) return false;
    } else if (dateRange.end && apt.scheduledDate) {
      const aptDate = new Date(apt.scheduledDate);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      if (aptDate > endDate) return false;
    }

    return true;
  }).sort((a, b) => {
    // Sorting functionality
    switch (sortBy) {
      case "newest":
        return new Date(b.scheduledDate || 0) - new Date(a.scheduledDate || 0);
      case "oldest":
        return new Date(a.scheduledDate || 0) - new Date(b.scheduledDate || 0);
      case "name-asc":
        return (a.studentId?.name || "").localeCompare(b.studentId?.name || "");
      case "name-desc":
        return (b.studentId?.name || "").localeCompare(a.studentId?.name || "");
      case "duration-high":
        return (b.duration || 0) - (a.duration || 0);
      case "duration-low":
        return (a.duration || 0) - (b.duration || 0);
      case "counselor-asc":
        return (a.counselor || "").localeCompare(b.counselor || "");
      case "counselor-desc":
        return (b.counselor || "").localeCompare(a.counselor || "");
      default:
        return 0;
    }
  });

  // Count appointments by type and status
  const appointmentStats = {
    total: appointments.length,
    zoom: appointments.filter((apt) => apt.appointmentType === "zoom").length,
    office: appointments.filter((apt) => apt.appointmentType === "office").length,
    confirmed: appointments.filter((apt) => apt.status === "confirmed").length,
    pending: appointments.filter((apt) => apt.status === "pending").length,
    completed: appointments.filter((apt) => apt.status === "completed").length,
  };

  if (loading) return <Loader />;

  const pageStyle = {
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.95)",
  };

  const statCardStyle = {
    borderRadius: "12px",
    border: "none",
    color: "white",
    padding: "20px",
  };

  const badgeStyle = {
    borderRadius: "20px",
    padding: "8px 16px",
    fontSize: "0.9rem",
  };

  const filterSectionStyle = {
    background: "#f8f9fa",
    borderRadius: "10px",
    padding: "20px",
    marginTop: "20px",
  };

  return (
    <div style={pageStyle}>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold text-gradient">
                <Calendar className="me-3" size={32} />
                Appointment Management
              </h2>
              <p className="text-muted mb-0">
                Manage all student appointments and consultations
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <Row className="mb-4 g-3">
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Calendar size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{appointmentStats.total}</h2>
                      <p className="mb-0 opacity-75">Total Appointments</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">
                        {appointmentStats.confirmed}
                      </h2>
                      <p className="mb-0 opacity-75">Confirmed</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ClockHistory size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">
                        {appointmentStats.pending}
                      </h2>
                      <p className="mb-0 opacity-75">Pending</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CalendarCheck size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">
                        {appointmentStats.completed}
                      </h2>
                      <p className="mb-0 opacity-75">Completed</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Search and Filter Bar */}
          <Card className="mb-4" style={cardStyle}>
            <Card.Body className="p-4">
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <div
                    className="input-group"
                    style={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <span className="input-group-text bg-white border-end-0">
                      <Search size={20} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search by ID, student name, email, phone, counselor, location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ border: "none", fontSize: "16px" }}
                    />
                  </div>
                </Col>
                <Col md={2}>
                  <div className="d-flex align-items-center">
                    <Filter className="me-2 text-muted" size={20} />
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="all">All Status</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="rescheduled">Rescheduled</option>
                    </select>
                  </div>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowFilters(!showFilters)}
                    className="w-100"
                    style={{ borderRadius: "10px" }}
                  >
                    <Filter className="me-2" />
                    {showFilters ? 'Hide Filters' : 'More Filters'}
                    <ChevronDown className="ms-2" />
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    variant="primary"
                    onClick={handleExportCSV}
                    className="w-100"
                    style={{ borderRadius: "10px" }}
                  >
                    <Download className="me-2" />
                    Export CSV
                  </Button>
                </Col>
              </Row>

              {/* Advanced Filters Section */}
              {showFilters && (
                <div style={filterSectionStyle}>
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Calendar className="me-1" size={14} />
                          Date Range
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                          className="mb-2"
                          placeholder="Start Date"
                        />
                        <Form.Control
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                          placeholder="End Date"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <GeoAlt className="me-1" size={14} />
                          City
                        </Form.Label>
                        <Form.Select
                          value={selectedCity}
                          onChange={(e) => setSelectedCity(e.target.value)}
                        >
                          <option value="">All Cities</option>
                          {availableCities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Person className="me-1" size={14} />
                          Counselor
                        </Form.Label>
                        <Form.Select
                          value={selectedCounselor}
                          onChange={(e) => setSelectedCounselor(e.target.value)}
                        >
                          <option value="">All Counselors</option>
                          {availableCounselors.map(counselor => (
                            <option key={counselor} value={counselor}>{counselor}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Clock className="me-1" size={14} />
                          Duration (minutes)
                        </Form.Label>
                        <Row className="g-2">
                          <Col xs={6}>
                            <Form.Control
                              type="number"
                              placeholder="Min"
                              value={minDuration}
                              onChange={(e) => setMinDuration(e.target.value)}
                            />
                          </Col>
                          <Col xs={6}>
                            <Form.Control
                              type="number"
                              placeholder="Max"
                              value={maxDuration}
                              onChange={(e) => setMaxDuration(e.target.value)}
                            />
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <SortDown className="me-1" size={14} />
                          Sort By
                        </Form.Label>
                        <Form.Select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="name-asc">Student Name (A-Z)</option>
                          <option value="name-desc">Student Name (Z-A)</option>
                          <option value="duration-high">Duration (High to Low)</option>
                          <option value="duration-low">Duration (Low to High)</option>
                          <option value="counselor-asc">Counselor (A-Z)</option>
                          <option value="counselor-desc">Counselor (Z-A)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-medium">
                          <Calendar className="me-1" size={14} />
                          Appointment Type
                        </Form.Label>
                        <Form.Select
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="zoom">Phone Call</option>
                          <option value="office">Office</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Button
                        variant="outline-secondary"
                        onClick={clearFilters}
                        className="w-100"
                        style={{ borderRadius: "10px" }}
                      >
                        <X className="me-2" />
                        Clear All Filters
                      </Button>
                    </Col>
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Tabs Section */}
          <Card className="mb-4" style={cardStyle}>
            <Card.Body className="p-3">
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="border-0"
              >
                <Tab
                  eventKey="zoom"
                  title={
                    <div className="d-flex align-items-center py-2">
                      <Telephone className="me-2" size={20} />
                      Phone Calls
                      <Badge
                        bg="info"
                        className="ms-2 rounded-pill"
                        style={badgeStyle}
                      >
                        {appointmentStats.zoom}
                      </Badge>
                    </div>
                  }
                  tabClassName="border-0 me-3"
                />
                <Tab
                  eventKey="office"
                  title={
                    <div className="d-flex align-items-center py-2">
                      <GeoAlt className="me-2" size={20} />
                      Office Consultations
                      <Badge
                        bg="primary"
                        className="ms-2 rounded-pill"
                        style={badgeStyle}
                      >
                        {appointmentStats.office}
                      </Badge>
                    </div>
                  }
                  tabClassName="border-0 me-3"
                />
              </Tabs>
            </Card.Body>
          </Card>

          {/* Appointment Table */}
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="fw-bold mb-1">
                    {activeTab === "zoom" && <Telephone className="me-2" />}
                    {activeTab === "office" && <GeoAlt className="me-2" />}
                    {activeTab === "zoom" && "Phone Calls"}
                    {activeTab === "office" && "Office Consultations"}
                  </h5>
                  <p className="text-muted mb-0">
                    Showing {filteredAppointments.length} of{" "}
                    {appointmentStats[activeTab]} appointments
                    {(selectedCity || selectedCounselor || dateRange.start || dateRange.end) && 
                      " (filtered)"}
                  </p>
                </div>
              </div>

              <div
                className="table-responsive"
                style={{ borderRadius: "10px", overflow: "hidden" }}
              >
                <Table hover className="mb-0">
                  <thead
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <tr>
                      <th className="ps-4 py-3">
                        <div className="d-flex align-items-center">
                          <SortDown className="me-2" />
                          Appointment ID
                        </div>
                      </th>
                      <th className="py-3">Student</th>
                      <th className="py-3">City</th>
                      <th className="py-3">Date & Time</th>
                      <th className="py-3">Counselor</th>
                      <th className="py-3">Status</th>
                      <th className="py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.length > 0 ? (
                      filteredAppointments.map((apt) => (
                        <tr
                          key={apt._id}
                          style={{
                            transition: "all 0.3s ease",
                            borderBottom: "1px solid #f1f1f1",
                          }}
                        >
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  background: "#f8f9fa",
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginRight: "12px",
                                }}
                              >
                                <Calendar size={18} className="text-primary" />
                              </div>
                              <div>
                                <div className="fw-bold">
                                  {apt.appointmentId}
                                </div>
                                <div className="small text-muted">
                                  {getTypeBadge(apt.appointmentType)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {apt.studentId?.name || "N/A"}
                            </div>
                            <div className="small text-muted">
                              {apt.studentId?.email || "N/A"}
                            </div>
                            <div className="small text-muted">
                              {apt.studentId?.phone ||
                                apt.studentId?.contact ||
                                apt.studentId?.phoneNumber ||
                                "N/A"}
                            </div>
                          </td>
                          <td>
                            <div className="small">
                              {apt.studentId?.city || "N/A"}
                            </div>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {formatDateTime(
                                apt.scheduledDate,
                                apt.scheduledTime,
                              )}
                            </div>
                            {apt.duration && (
                              <div className="small text-muted">
                                {apt.duration} minutes
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  background:
                                    "linear-gradient(135deg, #28a74515 0%, #20c99715 100%)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginRight: "8px",
                                  color: "#28a745",
                                }}
                              >
                                <Person size={16} />
                              </div>
                              <span className="fw-medium">
                                {apt.counselor || "Not assigned"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="link"
                              className="p-0"
                              style={{ textDecoration: "none" }}
                            >
                              {getStatusBadge(apt.status)}
                            </Button>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center"
                                onClick={() => handleViewClick(apt)}
                                style={{ borderRadius: "8px" }}
                              >
                                <Eye className="me-1" /> View Details
                              </Button>

                              {(apt.studentId?.phone ||
                                apt.studentId?.contact ||
                                apt.studentId?.phoneNumber ||
                                apt.phoneNumber) && (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  onClick={() => {
                                    const num = formatPhoneForWhatsapp(
                                      apt.studentId?.phone ||
                                        apt.studentId?.contact ||
                                        apt.studentId?.phoneNumber ||
                                        apt.phoneNumber,
                                    );
                                    if (num) {
                                      window.open(
                                        `https://wa.me/${num}`,
                                        "_blank",
                                      );
                                    }
                                  }}
                                  style={{ borderRadius: "8px" }}
                                >
                                  <Whatsapp className="me-1" /> WhatsApp
                                </Button>
                              )}
                              {apt.studentId?.email && (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  onClick={() => {
                                    const email = apt.studentId?.email;
                                    const subject = encodeURIComponent(
                                      "Regarding your appointment",
                                    );
                                    const body = encodeURIComponent("Hello,");
                                    window.open(
                                      `mailto:${email}?subject=${subject}&body=${body}`,
                                    );
                                  }}
                                  style={{ borderRadius: "8px" }}
                                >
                                  <Envelope className="me-1" /> Email
                                </Button>
                              )}
                              {apt.appointmentType === "zoom" &&
                                apt.status === "confirmed" &&
                                (apt.phoneNumber || apt.zoomLink) && (
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="d-flex align-items-center"
                                    onClick={() => {
                                      if (apt.phoneNumber) {
                                        window.open(`tel:${apt.phoneNumber}`);
                                      } else {
                                        window.open(apt.zoomLink, "_blank");
                                      }
                                    }}
                                    style={{ borderRadius: "8px" }}
                                  >
                                    <Telephone className="me-1" /> Call
                                  </Button>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <div className="py-4">
                            <Search size={48} className="text-muted mb-3" />
                            <h5 className="text-muted">
                              No appointments found
                            </h5>
                            <p className="text-muted mb-0">
                              Try adjusting your filters
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* View Appointment Details Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <Eye size={24} />
              </div>
              <div>
                Appointment Details
                {selectedAppointment && (
                  <div className="small text-muted mt-1">
                    ID: {selectedAppointment.appointmentId}
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {selectedAppointment && (
            <div>
              <Row className="mb-4 g-4">
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <h6 className="fw-semibold mb-3 text-primary">
                        <Calendar className="me-2" />
                        Appointment Information
                      </h6>
                      {getAppointmentDetails(selectedAppointment).map(
                        (detail, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center mb-3"
                          >
                            <div className="me-3" style={{ color: "#4361ee" }}>
                              {detail.icon}
                            </div>
                            <div>
                              <div className="fw-medium">
                                {detail.text.split(": ")[0]}
                              </div>
                              <div className="text-muted">
                                {detail.text.split(": ")[1]}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <h6 className="fw-semibold mb-3 text-primary">
                        <Person className="me-2" />
                        Student Information
                      </h6>
                      <div className="d-flex align-items-center mb-3">
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            background:
                              "linear-gradient(135deg, #28a74515 0%, #20c99715 100%)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "15px",
                            color: "#28a745",
                          }}
                        >
                          <Person size={24} />
                        </div>
                        <div>
                          <div className="fw-bold">
                            {selectedAppointment.studentId?.name || "N/A"}
                          </div>
                          <div className="text-muted">
                            {selectedAppointment.studentId?.email || "N/A"}
                          </div>
                          <div className="text-muted mt-2 d-flex align-items-center">
                            <Telephone className="me-2" />
                            <small>
                              {selectedAppointment.studentId?.phone ||
                                selectedAppointment.studentId?.contact ||
                                selectedAppointment.studentId?.phoneNumber ||
                                "N/A"}
                            </small>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {selectedAppointment.notes && (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Body>
                    <h6 className="fw-semibold mb-3 text-primary">
                      <FileText className="me-2" />
                      Notes
                    </h6>
                    <p className="mb-0">{selectedAppointment.notes}</p>
                  </Card.Body>
                </Card>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-success"
                  className="px-4 py-2"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditClick(selectedAppointment);
                  }}
                  style={{ borderRadius: "10px" }}
                >
                  <Pencil className="me-1" /> Edit Appointment
                </Button>
                <Button
                  variant="outline-primary"
                  className="px-4 py-2"
                  onClick={() => {
                    setShowViewModal(false);
                    handleStatusClick(selectedAppointment);
                  }}
                  style={{ borderRadius: "10px" }}
                >
                  Update Status
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Appointment Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <Pencil size={24} />
              </div>
              <div>
                Edit Appointment
                {selectedAppointment && (
                  <div className="small text-muted mt-1">
                    ID: {selectedAppointment.appointmentId} | Student:{" "}
                    {selectedAppointment.studentId?.name}
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Calendar className="me-1" />
                    Scheduled Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={editForm.scheduledDate}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        scheduledDate: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Clock className="me-1" />
                    Scheduled Time
                  </Form.Label>
                  <Form.Control
                    type="time"
                    value={editForm.scheduledTime}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        scheduledTime: e.target.value,
                      })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3 g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">
                    <Person className="me-1" />
                    Counselor
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter counselor name"
                    value={editForm.counselor}
                    onChange={(e) =>
                      setEditForm({ ...editForm, counselor: e.target.value })
                    }
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">
                <FileText className="me-1" />
                Notes
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add any notes or special instructions"
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm({ ...editForm, notes: e.target.value })
                }
                className="py-2"
                style={{ borderRadius: "8px" }}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="light"
                onClick={() => setShowEditModal(false)}
                className="px-4 me-2"
                style={{ borderRadius: "10px" }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleEditSubmit}
                className="px-4"
                style={{
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Status Update Modal */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            Update Appointment Status
          </Modal.Title>
          {selectedAppointment && (
            <div className="small text-muted mt-1">
              ID: {selectedAppointment.appointmentId} | Type:{" "}
              {selectedAppointment.appointmentType}
            </div>
          )}
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-medium">Select Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="py-2"
              style={{ borderRadius: "8px" }}
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rescheduled">Rescheduled</option>
            </Form.Select>
          </Form.Group>
          {selectedAppointment && (
            <div
              className="mt-3 p-3"
              style={{ background: "#f8f9fa", borderRadius: "8px" }}
            >
              <small className="text-muted d-block">
                Student: {selectedAppointment.studentId?.name || "N/A"}
              </small>
              <small className="text-muted d-block mt-1">
                Current:{" "}
                {selectedAppointment.scheduledDate
                  ? formatDateTime(
                      selectedAppointment.scheduledDate,
                      selectedAppointment.scheduledTime,
                    )
                  : "Not scheduled"}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowStatusModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStatusUpdate}
            className="px-4"
            style={{
              borderRadius: "10px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              border: "none",
            }}
          >
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Documents Modal - Only for Assessments */}
      <Modal
        show={showDocsModal}
        onHide={() => setShowDocsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <FileText size={24} />
              </div>
              <div>
                Assessment Documents
                {selectedAppointment && (
                  <div className="small text-muted mt-1">
                    Appointment ID: {selectedAppointment.appointmentId}
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {documents.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead
                  style={{
                    background: "#f8f9fa",
                    color: "#495057",
                  }}
                >
                  <tr>
                    <th className="ps-4 py-3">File Name</th>
                    <th className="py-3">Type</th>
                    <th className="py-3">Size</th>
                    <th className="py-3">Uploaded</th>
                    <th className="pe-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={index}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <FileText className="me-3 text-primary" />
                          <span className="fw-medium">{doc.fileName}</span>
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={
                            doc.type === "passport"
                              ? "primary"
                              : doc.type === "bank_statement"
                                ? "success"
                                : doc.type === "academic_records"
                                  ? "info"
                                  : doc.type === "sop"
                                    ? "warning"
                                    : doc.type === "cv"
                                      ? "secondary"
                                      : "light"
                          }
                          text="dark"
                          className="px-3 py-1 rounded-pill"
                        >
                          {doc.type}
                        </Badge>
                      </td>
                      <td>{(doc.fileSize / 1024).toFixed(2)} KB</td>
                      <td>{new Date(doc.uploadedAt).toLocaleString()}</td>
                      <td className="pe-4">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() =>
                            window.open(
                              `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000"}/${doc.filePath}`,
                              "_blank",
                            )
                          }
                          style={{ borderRadius: "8px" }}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <FileText size={64} className="text-muted mb-3" />
              <h5 className="text-muted">
                No documents found for this assessment
              </h5>
              <Button
                variant="outline-primary"
                className="mt-2"
                style={{ borderRadius: "10px" }}
                onClick={() =>
                  window.open(
                    `/documents/${selectedAppointment?.appointmentId}`,
                    "_blank",
                  )
                }
              >
                Upload Documents
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowDocsModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AppointmentManager;